'use client'

/**
 * Refresh typography presets for use directly on Mantine <Title> and <Text>.
 * Only shared, reusable typography lives here; set color and layout (maw, mt, mb, etc.) at the call site.
 *
 * Example:
 *   <Title {...refreshTitleVariantProps.hero} mt={36} mb={16} maw={533}>{title}</Title>
 *   <Text {...refreshTextVariantProps.body} c="white" dangerouslySetInnerHTML={{ __html: subtitle }} styles={refreshTextBodyHtmlStyles} />
 *   <Text {...refreshTextVariantProps.body} c="gray.6">{description}</Text>
 */

import type { TextProps, TitleProps } from '@mantine/core'

/** Title variants: typography + semantic color. Override c at call site if needed. */
export type RefreshTitleVariant =
    | 'hero'
    | 'hero2xl'
    | 'section'
    | 'sectionLg'
    | 'sectionXl'
    | 'section2xl'
    | 'genAi'
    | 'card'

/**
 * Text variants: typography only. Pass c (color) and layout at call site.
 * - body: default body (lg, 130%); use with c="gray.6" or c="white"
 * - bodyLead: subtitle/lead (md, 150%); use with c at call site
 * - bodyCompact: tight (16px, 130%)
 * - statValue / statValueMega / cardTitle / cardDescription / accordionItemTitle: specific UI bits
 */
export type RefreshTextVariant =
    | 'body'
    | 'bodyLead'
    | 'bodyCompact'
    | 'statValue'
    | 'statValueMega'
    | 'cardTitle'
    | 'cardDescription'
    | 'accordionItemTitle'

export const refreshTitleVariantProps: Record<RefreshTitleVariant, Partial<TitleProps>> = {
    hero: {
        order: 2,
        c: 'white',
        fw: 400,
        fz: { base: 'var(--gm-refresh-title-md)', sm: 'var(--gm-refresh-title-lg)' },
        lh: { base: 'var(--gm-refresh-title-md-lh)', sm: 'var(--gm-refresh-title-lg-lh)' },
        lts: -1.6,
        m: 0,
    },
    hero2xl: {
        order: 2,
        c: 'white',
        fw: 400,
        fz: { base: 'var(--gm-refresh-title-md)', sm: 'var(--gm-refresh-title-2xl)' },
        lh: { base: 'var(--gm-refresh-title-md-lh)', sm: 'var(--gm-refresh-title-2xl-lh)' },
        lts: -2.1,
        m: 0,
    },
    section: {
        order: 2,
        c: 'gray.9',
        fw: 400,
        fz: 'var(--gm-refresh-title-md)',
        lh: '100%',
        lts: -0.8,
        m: 0,
    },
    sectionLg: {
        order: 2,
        c: 'gray.9',
        fw: 400,
        fz: { base: 'var(--gm-refresh-title-sm)', sm: 'var(--gm-refresh-title-lg)' },
        lh: { base: 'var(--gm-refresh-title-sm-lh)', sm: 'var(--gm-refresh-title-lg-lh)' },
        lts: -0.96,
        m: 0,
    },
    sectionXl: {
        order: 2,
        c: 'gray.9',
        fw: 400,
        fz: { base: 'var(--gm-refresh-title-md)', sm: 'var(--gm-refresh-title-xl)' },
        lh: 'var(--gm-refresh-title-xl-lh)',
        lts: -1.6,
        m: 0,
    },
    section2xl: {
        order: 2,
        c: 'gray.9',
        fw: 400,
        fz: { base: 'var(--gm-refresh-title-md)', sm: 'var(--gm-refresh-title-2xl)' },
        lh: '100%',
        lts: -1.6,
        m: 0,
    },
    genAi: {
        order: 2,
        c: 'gray.1',
        fw: 400,
        fz: 'var(--gm-refresh-title-md)',
        lh: 'var(--gm-refresh-title-md-lh)',
        lts: -1.6,
        m: 0,
    },
    card: {
        order: 2,
        c: 'white',
        fw: 450,
        fz: 'var(--gm-refresh-title-card)',
        lh: '110%',
        lts: -0.28,
        m: 0,
    },
}

/** Typography-only; pass component (e.g. component="div") and c at call site when needed. */
export const refreshTextVariantProps: Record<RefreshTextVariant, Partial<TextProps>> = {
    body: {
        size: 'lg',
        fw: 450,
        lh: '130%',
        m: 0,
    },
    bodyLead: {
        size: 'md',
        fw: 450,
        lh: '150%',
        lts: -0.14,
        m: 0,
    },
    bodyCompact: {
        fz: 16,
        fw: 450,
        lh: '130%',
        m: 0,
        mb: 16,
    },
    statValue: {
        fz: 48,
        fw: 450,
        lh: '100%',
        lts: -1.6,
    },
    statValueMega: {
        fw: 450,
        lh: '80%',
        lts: '-0.05em',
        c: 'white',
    },
    cardTitle: {
        c: 'white',
        m: 0,
        fz: 'var(--gm-refresh-title-card)',
        fw: 450,
        lh: '110%',
        lts: -0.28,
    },
    cardDescription: {
        c: 'white',
        m: 0,
        size: 'md',
        fw: 400,
        lh: '130%',
        opacity: 0.95,
        lts: -0.32,
    },
    accordionItemTitle: {
        c: 'white',
        fz: 'var(--gm-refresh-title-xs)',
        fw: 450,
        lh: '110%',
        lts: -0.24,
        mb: 14,
    },
}

/** Use with body/bodyLead/bodyCompact when rendering HTML (dangerouslySetInnerHTML). */
export const refreshTextBodyHtmlStyles: TextProps['styles'] = {
    root: {
        '& b, & strong': { fontWeight: 550 },
        '& p': { display: 'block', margin: 0, marginBottom: '0.5em' },
        '& p:last-child': { marginBottom: 0 },
    },
}

/** Optional: stronger bold for HTML body (e.g. CTAs). */
export const refreshTextBodyHtmlStylesBold: TextProps['styles'] = {
    root: {
        '& b, & strong': { fontWeight: 600 },
        '& p': { display: 'block', margin: 0, marginBottom: '0.5em' },
        '& p:last-child': { marginBottom: 0 },
    },
}

/** Use with statValueMega when it’s an overlay (position at call site). */
export const refreshTextStatValueMegaStyles: TextProps['styles'] = {
    root: {
        fontSize: 'clamp(100px, 14vw, 200px)',
        pointerEvents: 'none',
        zIndex: 1,
    },
}
